import { QueryObserverResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';

import {
  getHostingAdvancedImagesQueryKey,
  getHostingSitesV2QueryKey,
  getUserHostingQueryKey,
} from '@newfold/huapi-js';

import { queryClient } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import {
  HOSTING_FETCH_LIMIT,
  HOSTING_LIST_PAGE_SS_KEY,
} from '~/components/TenantProvider/utils/constants';

import {
  MAX_POLL_COUNT,
  POLL_INTERVAL,
  REIMAGE_FAILED_STATUSES,
  REIMAGE_IN_PROGRESS_STATUSES,
} from '../utils/constants';

type RefetchServerStatus = () => Promise<
  QueryObserverResult<string | undefined, AxiosError>
>;

type ServerStatus = string;

// Type guard to check if a status is in the reimage failed statuses
const isReimageFailedStatus = (
  status: string | undefined,
): status is ServerStatus => {
  return status !== undefined && REIMAGE_FAILED_STATUSES.includes(status);
};

// Type guard to check if a status is in the reimage in progress statuses
const isReimageInProgressStatus = (
  status: string | undefined,
): status is ServerStatus => {
  return status !== undefined && REIMAGE_IN_PROGRESS_STATUSES.includes(status);
};

interface UseReimagePollingParams {
  hostingId: string;
  isReimaging: boolean;
  refetchServerStatus: RefetchServerStatus;
  onStatusChange: (isReimaging: boolean, reimageFailed: boolean) => void;
}

/**
 * Hook to handle polling server status during reimage process
 * Polls every 10 seconds until status becomes active or max calls reached
 */
export const useReimagePolling = ({
  hostingId,
  isReimaging,
  refetchServerStatus,
  onStatusChange,
}: UseReimagePollingParams): void => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCountRef = useRef(0);

  // Helper function to clear polling interval and reset count
  const clearPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingCountRef.current = 0;
  };

  // Helper function to stop polling and reset state
  const stopPolling = (reimageFailed: boolean = false) => {
    onStatusChange(false, reimageFailed);
    clearPolling();
    const nextPage = Number(
      sessionStorage.getItem(HOSTING_LIST_PAGE_SS_KEY) ?? 1,
    );

    queryClient.invalidateQueries({
      queryKey: getHostingAdvancedImagesQueryKey(String(hostingId)),
    });
    queryClient.invalidateQueries({
      queryKey: getUserHostingQueryKey({
        page: 1,
        limit: nextPage * HOSTING_FETCH_LIMIT,
      }),
    });
    queryClient.invalidateQueries({
      queryKey: getUserHostingQueryKey({
        page: nextPage,
        limit: 3,
      }),
    });
    queryClient.invalidateQueries({
      queryKey: getHostingSitesV2QueryKey(hostingId),
    });
  };

  // Helper function to just stop polling without changing state
  const stopPollingOnly = () => {
    clearPolling();
  };

  useEffect(() => {
    if (!isReimaging) {
      // Clear interval if reimaging is stopped
      clearPolling();
      return;
    }

    const checkServerStatus = async () => {
      try {
        pollingCountRef.current += 1;
        const result = await refetchServerStatus();
        const currentStatus = result?.data;

        // Determine if we should stop polling and the reason
        const maxCountReached = pollingCountRef.current >= MAX_POLL_COUNT;
        const isStillInProgress = isReimageInProgressStatus(currentStatus);

        // If max count reached but status is still in progress, just stop polling
        if (maxCountReached && isStillInProgress) {
          stopPollingOnly();
          return;
        }

        // Otherwise, stop polling and update state
        const shouldStop =
          !currentStatus ||
          currentStatus === 'active' ||
          isReimageFailedStatus(currentStatus) ||
          !isStillInProgress;

        if (shouldStop) {
          const reimageFailed = isReimageFailedStatus(currentStatus);
          stopPolling(reimageFailed);
        }
        // If status is still in progress, continue polling (no action needed)
      } catch (error) {
        // On error, stop polling
        stopPolling(false);
      }
    };

    // Start polling immediately, then set interval
    checkServerStatus();
    pollingIntervalRef.current = setInterval(checkServerStatus, POLL_INTERVAL);

    // Cleanup on unmount or when reimaging stops
    return () => {
      clearPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReimaging, refetchServerStatus, onStatusChange]);
};
